import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, Tag, Mail, Link2, Loader2, X, Search, Plus, Trash2, Save, Share2, Copy, FileStack, RefreshCw,
} from 'lucide-react';
import BudgetPlanner, { money, budgetTotals, quarterLabels, renderResolution } from './BudgetEditor';
import FinanceViewer from '../olfin/FinanceViewer';
import { TeamCosts } from './org/OrgTeamsPanel';

/**
 * AD Finance — the Athletic Director's (and Treasurer's) money-management page
 * (docs/AD_DASHBOARD_AND_FINANCE.md §2). Distinct from OL Finance (the books).
 *   Player Dues — one row per registration, read from the registration's own bill;
 *                 discounts go through registration billing; late reminders (X days).
 *   Budget      — budgets from the org's templates, member edit links.
 *   Reports     — the Treasurer's read-only report viewer (when granted).
 * Condensed (OL_UI_DESIGN_GUIDE §6a): one summary strip, lists scroll in-panel.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const errMsg = (e, d) => e?.response?.data?.detail || d;

const STATUS = {
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  partial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  due: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'cash due': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'not billed yet': 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  waived: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  'no fee': 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
};
const FILTERS = [['all', 'All'], ['overdue', 'Overdue'], ['partial', 'Partial'], ['due', 'Due'], ['not billed yet', 'Not billed'], ['paid', 'Paid']];

const inputCls = 'rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-2 py-1.5 text-sm';
const btn = 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50';
const btnPrimary = `${btn} bg-blue-600 text-white hover:bg-blue-700`;
const btnGhost = `${btn} border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700`;

export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-auto" onClick={onClose}>
      <div className={`mt-10 w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white truncate">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 text-slate-800 dark:text-slate-200">{children}</div>
      </div>
    </div>
  );
}

export default function AdFinancePanel({ orgId, showReports = false, showTeamCosts = false, onOpenTeams, reportsOrgId, isDark }) {
  const [tab, setTab] = useState('dues');
  // Team Costs = the same screen as Teams ▸ Team Costs (AD only; AJ 2026-10-20).
  const tabs = [['dues', 'Player Dues'], ['budget', 'Budget'], ...(showTeamCosts ? [['costs', 'Team Costs']] : []),
    ...(showReports ? [['reports', 'Reports']] : [])];
  return (
    <div data-testid="ad-finance-panel">
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 mb-3">
        <Wallet className="h-5 w-5 text-emerald-600" />
        {tabs.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`ad-finance-tab-${k}`}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 ${tab === k
              ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'dues' && <PlayerDues orgId={orgId} />}
      {tab === 'budget' && <Budgets orgId={orgId} />}
      {tab === 'costs' && showTeamCosts && <TeamCosts orgId={orgId} isDark={isDark} onGoLevels={() => onOpenTeams?.()} />}
      {tab === 'reports' && showReports && (
        <FinanceViewer orgId={reportsOrgId || orgId} isDark={isDark} title="Finance — Report Viewer" canEdit={true} />
      )}
    </div>
  );
}

// ══ Player Dues ════════════════════════════════════════════════════════════
function PlayerDues({ orgId }) {
  const [data, setData] = useState(null);
  const [formId, setFormId] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [disc, setDisc] = useState(null);       // row
  const [remind, setRemind] = useState(false);
  const base = `${API_URL}/api/ad-finance/${orgId}`;

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${base}/dues`, { params: formId ? { form_id: formId } : {} });
      setData(d);
    } catch (e) { toast.error(errMsg(e, 'Could not load player dues')); setData({ rows: [], summary: {}, forms: [] }); }
  }, [base, formId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => (filter === 'all' || r.status === filter || (filter === 'due' && r.status === 'cash due'))
      && (!s || `${r.player} ${r.family} ${r.contact} ${r.email} ${r.team} ${r.form}`.toLowerCase().includes(s)));
  }, [data, q, filter]);
  const sm = data?.summary || {};

  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success('Pay link copied'); };

  return (
    <div className="space-y-2" data-testid="ad-finance-dues">
      <div className="flex flex-wrap items-center gap-2">
        <select value={formId} onChange={(e) => setFormId(e.target.value)} className={`${inputCls} py-1 max-w-[240px]`} data-testid="dues-form">
          <option value="">All registrations</option>
          {(data?.forms || []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Player, family, team…" className={`${inputCls} py-1 pl-7 w-48`} data-testid="dues-search" />
        </div>
        <div className="flex gap-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`dues-filter-${k.replace(/\s+/g, '-')}`}
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${filter === k ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button type="button" className={`${btnGhost} py-1 px-2`} onClick={load} title="Refresh"><RefreshCw className="h-4 w-4" /></button>
          <button type="button" className={`${btnPrimary} py-1`} onClick={() => setRemind(true)} data-testid="dues-remind-open">
            <Mail className="h-4 w-4" /> Late reminders
          </button>
        </div>
      </div>

      {/* one line of totals (condensed, OL_UI_DESIGN_GUIDE §6a) */}
      <div className="flex flex-wrap gap-1.5 text-xs" data-testid="dues-summary">
        {[['Players', sm.players ?? '—', 'text-slate-900 dark:text-white'], ['Billed', money(sm.billed), 'text-slate-900 dark:text-white'],
          ['Discounts', money(sm.discounts), 'text-slate-900 dark:text-white'], ['Collected', money(sm.collected), 'text-emerald-600 dark:text-emerald-400'],
          ['Outstanding', money(sm.outstanding), 'text-amber-600 dark:text-amber-400'],
          ['Overdue', `${money(sm.overdue)} · ${sm.overdue_families ?? 0} families`, 'text-red-600 dark:text-red-400']].map(([l, v, c]) => (
          <span key={l} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-2 py-0.5">
            <span className="text-slate-500 dark:text-slate-400">{l}</span> <b className={`tabular-nums ${c}`}>{v}</b>
          </span>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-auto max-h-[calc(100vh-270px)] min-h-[200px] bg-white dark:bg-slate-900/40">
        {!data ? <div className="p-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
          <table className="w-full text-xs" data-testid="dues-table">
            <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <tr>
                <th className="text-left px-2 py-1">Player</th><th className="text-left px-1.5">Family</th><th className="text-left px-1.5">Team</th>
                <th className="text-right px-1.5">Fee</th><th className="text-right px-1.5">Disc.</th><th className="text-right px-1.5">Due</th>
                <th className="text-right px-1.5">Paid</th><th className="text-right px-1.5">Balance</th><th className="text-left px-1.5">Status</th>
                <th className="text-left px-1.5">Next due</th><th className="px-1" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 whitespace-nowrap" data-testid={`dues-row-${r.id}`}>
                  <td className="px-2 py-0.5 font-medium max-w-[160px] truncate" title={r.form}>{r.player}</td>
                  <td className="px-1.5 max-w-[150px] truncate text-slate-600 dark:text-slate-400" title={r.email || ''}>{r.family}</td>
                  <td className="px-1.5 text-[11px] text-slate-600 dark:text-slate-400">{r.team}</td>
                  <td className="px-1.5 text-right tabular-nums">{money(r.fee)}</td>
                  <td className="px-1.5 text-right tabular-nums" title={(r.ad_discounts || []).map((d) => `${d.label} ${money(d.amount)}`).join(', ')}>
                    {r.reg_discount + r.ad_discount_total > 0 ? `−${money(r.reg_discount + r.ad_discount_total)}` : '—'}
                  </td>
                  <td className="px-1.5 text-right tabular-nums">{money(r.due)}</td>
                  <td className="px-1.5 text-right tabular-nums">{money(r.paid)}</td>
                  <td className="px-1.5 text-right tabular-nums font-semibold">{money(r.balance)}</td>
                  <td className="px-1.5">
                    <span className={`px-1.5 rounded-full text-[10px] font-medium ${STATUS[r.status] || STATUS.due}`}>{r.status}</span>
                    {r.days_overdue > 0 && <span className="ml-1 text-[10px] text-red-600 dark:text-red-400">{r.days_overdue}d</span>}
                  </td>
                  <td className="px-1.5 tabular-nums">
                    {r.next_due ? String(r.next_due).slice(0, 10) : '—'}
                    {r.last_reminder_at && <span title={`Reminder sent ${String(r.last_reminder_at).slice(0, 10)}`}><Mail className="h-3 w-3 inline ml-1 -mt-0.5 text-slate-400" /></span>}
                  </td>
                  <td className="px-1 text-right">
                    {r.pay_url && <button type="button" title="Copy pay link" onClick={() => copy(r.pay_url)} className="p-0.5 text-slate-400 hover:text-blue-600"><Link2 className="h-3.5 w-3.5" /></button>}
                    {r.status !== 'no fee' && (
                      <button type="button" title="Discounts" onClick={() => setDisc(r)} data-testid={`dues-discount-${r.id}`} className="p-0.5 text-slate-400 hover:text-emerald-600"><Tag className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={11} className="p-6 text-center text-slate-500">No registrations match.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      {disc && <DiscountDialog base={base} row={disc} onClose={() => setDisc(null)} onDone={() => { setDisc(null); load(); }} />}
      {remind && <RemindDialog base={base} formId={formId} forms={data?.forms || []} onClose={() => setRemind(false)} onDone={() => { setRemind(false); load(); }} />}
    </div>
  );
}

function DiscountDialog({ base, row, onClose, onDone }) {
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState('amount');
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const billed = !!row.payment_id;
  const add = async () => {
    setBusy(true);
    try {
      await axios.post(`${base}/dues/${row.id}/discounts`, { label, ...(mode === 'percent' ? { percent: Number(val) } : { amount: Number(val) }) });
      toast.success('Discount applied'); onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not apply the discount')); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    setBusy(true);
    try { await axios.delete(`${base}/dues/${row.id}/discounts/${d.id}`); toast.success('Discount removed'); onDone(); }
    catch (e) { toast.error(errMsg(e, 'Could not remove the discount')); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Discounts — ${row.player}`} onClose={onClose}>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
        {billed
          ? 'This registration is billed: a discount lowers the unpaid balance and re-spreads any installments. The family sees the new amount on their pay link.'
          : 'Not billed yet: the discount is taken off the bill when the registration is billed (when the player is selected).'}
      </p>
      <div className="text-sm mb-3 space-y-1">
        <div className="flex justify-between"><span>Registration fee</span><span className="tabular-nums">{money(row.fee)}</span></div>
        {row.reg_discount > 0 && <div className="flex justify-between text-slate-500"><span>Registration discounts (sibling, coach…)</span><span className="tabular-nums">−{money(row.reg_discount)}</span></div>}
        {(row.ad_discounts || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between">
            <span>{d.label}{d.percent ? ` (${d.percent}%)` : ''} <span className="text-[11px] text-slate-400">· {d.by_name} · {String(d.at).slice(0, 10)}{d.applied_to_payment ? ' · on the bill' : ' · when billed'}</span></span>
            <span className="flex items-center gap-2 tabular-nums">−{money(d.amount)}
              <button type="button" disabled={busy} onClick={() => remove(d)} className="text-slate-400 hover:text-red-600" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          </div>
        ))}
        <div className="flex justify-between font-semibold border-t border-slate-200 dark:border-slate-700 pt-1"><span>Balance</span><span className="tabular-nums">{money(row.balance)}</span></div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[160px] text-xs">Reason<input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Scholarship" className={`${inputCls} w-full mt-0.5`} data-testid="discount-label" /></label>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls}><option value="amount">$</option><option value="percent">%</option></select>
        <input type="number" min="0" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} className={`${inputCls} w-24`} data-testid="discount-value" />
        <button type="button" className={btnPrimary} disabled={busy || !val} onClick={add} data-testid="discount-apply">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />} Apply
        </button>
      </div>
    </Modal>
  );
}

function RemindDialog({ base, formId, forms, onClose, onDone }) {
  const [days, setDays] = useState(7);
  const [fid, setFid] = useState(formId || '');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = { days_late: Number(days) || 0, form_id: fid || null, message };
  const doPreview = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${base}/dues/remind`, { ...body, preview: true }); setPreview(data.families || []); }
    catch (e) { toast.error(errMsg(e, 'Could not build the list')); } finally { setBusy(false); }
  };
  useEffect(() => { setPreview(null); }, [days, fid]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${base}/dues/remind`, body);
      toast.success(`Reminders sent to ${data.sent} of ${data.families} families${data.no_email?.length ? ` — no email for ${data.no_email.length}` : ''}`);
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not send the reminders')); } finally { setBusy(false); }
  };
  return (
    <Modal title="Late-dues reminders" onClose={onClose}>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-xs">Late by at least
          <span className="flex items-center gap-1 mt-0.5"><input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className={`${inputCls} w-20`} data-testid="remind-days" /> days</span>
        </label>
        <label className="text-xs flex-1 min-w-[180px]">Registration
          <select value={fid} onChange={(e) => setFid(e.target.value)} className={`${inputCls} w-full mt-0.5`}>
            <option value="">All registrations</option>
            {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </label>
      </div>
      <label className="text-xs block mb-3">Note added to the email (optional)
        <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputCls} w-full mt-0.5`} />
      </label>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">One email per family: each late player, balance, days late and their pay link.</p>
      {preview && (
        <div className="max-h-56 overflow-auto rounded border border-slate-200 dark:border-slate-700 mb-3" data-testid="remind-preview">
          {preview.length === 0 ? <div className="p-3 text-sm text-slate-500">No family is that late.</div> : (
            <table className="w-full text-xs">
              <tbody>{preview.map((f, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-2 py-1 font-medium">{f.family}</td>
                  <td className="px-2">{f.email || <span className="text-red-600">no email</span>}</td>
                  <td className="px-2">{f.players.join(', ')}</td>
                  <td className="px-2 text-right tabular-nums">{money(f.balance)}</td>
                  <td className="px-2 text-right">{f.max_days}d</td>
                </tr>))}</tbody>
            </table>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className={btnGhost} disabled={busy} onClick={doPreview} data-testid="remind-preview-btn">Preview list</button>
        <button type="button" className={btnPrimary} disabled={busy || !preview || !preview.length} onClick={send} data-testid="remind-send">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send {preview ? preview.filter((f) => f.email).length : ''} emails
        </button>
      </div>
    </Modal>
  );
}

// ══ Budget ═════════════════════════════════════════════════════════════════
function Budgets({ orgId }) {
  const base = `${API_URL}/api/ad-finance/${orgId}`;
  const [list, setList] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [starter, setStarter] = useState(null);
  const [sports, setSports] = useState([]);
  const [orgName, setOrgName] = useState('');
  const [cur, setCur] = useState(null);       // full budget being edited
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [statusAct, setStatusAct] = useState(null);

  const loadList = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([axios.get(`${base}/budgets`), axios.get(`${base}/budget-templates`)]);
      setList(b.data.budgets || []); setSports(b.data.sports || []); setOrgName(b.data.org || '');
      setTemplates(t.data.templates || []); setStarter(t.data.starter || null);
      return b.data.budgets || [];
    } catch (e) { toast.error(errMsg(e, 'Could not load budgets')); setList([]); return []; }
  }, [base]);
  const open = useCallback(async (id) => {
    try { const { data } = await axios.get(`${base}/budgets/${id}`); setCur(data.budget); setDirty(false); }
    catch (e) { toast.error(errMsg(e, 'Could not open the budget')); }
  }, [base]);
  useEffect(() => { loadList().then((l) => { if (l[0]) open(l[0].id); }); }, [loadList, open]);

  const pick = (id) => {
    if (dirty && !window.confirm('Discard unsaved changes to this budget?')) return;
    open(id);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${base}/budgets/${cur.id}`, {
        name: cur.name, season: cur.season, notes: cur.notes, sections: cur.sections,
        calculators: cur.calculators, settings: cur.settings, resolution: cur.resolution });
      setCur(data.budget); setDirty(false); toast.success('Budget saved'); loadList();
    } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete the budget “${cur.name}” and its approved versions? Its member links stop working.`)) return;
    try { await axios.delete(`${base}/budgets/${cur.id}`); setCur(null); const l = await loadList(); if (l[0]) open(l[0].id); }
    catch (e) { toast.error(errMsg(e, 'Could not delete')); }
  };
  const saveTemplate = async () => {
    const name = window.prompt('Template name (keeps the sections, line names, calculators, unit costs and resolution — no amounts or quantities):', cur.name);
    if (!name) return;
    try { await axios.post(`${base}/budget-templates`, { name, from_budget_id: cur.id }); toast.success('Template saved'); loadList(); }
    catch (e) { toast.error(errMsg(e, 'Could not save the template')); }
  };
  const edit = (patch) => { setCur((c) => ({ ...c, ...patch })); setDirty(true); };
  // Budget bridge (§2b-3): OL Finance accounts for linking lines, and saving the links (any status).
  const loadAccounts = async () => (await axios.get(`${base}/budgets/${cur.id}/finance-accounts`)).data;
  const saveLinks = async (links) => {
    if (dirty) { toast.error('Save your budget changes first, then save the links'); throw new Error('dirty'); }
    try {
      const { data } = await axios.put(`${base}/budgets/${cur.id}/links`, { links });
      setCur(data.budget);
      toast.success('Links saved — actuals now come from OL Finance'); loadList();
    } catch (e) { toast.error(errMsg(e, 'Could not save the links')); throw e; }
  };
  const setSetting = (k, v) => edit({ settings: { ...(cur.settings || {}), [k]: v } });
  const fill = async (source) => {
    if (!cur.season) { toast.error('Set the budget season first (e.g. 2026-2027)'); return []; }
    try {
      const { data } = await axios.get(`${base}/budget-sources/${source}`, { params: { season: cur.season, ...(cur.settings?.sport ? { sport: cur.settings.sport } : {}) } });
      if (!data.rows.length) toast.info('OL has no teams for that season yet');
      return data.rows;
    } catch (e) { toast.error(errMsg(e, 'Could not load from OL')); return []; }
  };
  const print = () => {
    const t = budgetTotals(cur);
    const ql = quarterLabels(cur.settings?.fiscal_start_month || 1);
    const esc = (x) => String(x ?? '').replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
    const rows = [['Total income', t.income_q, t.income_planned], ['Total expenses', t.expense_q, t.expense_planned], ['Balance', t.balance_q, t.net_planned]]
      .map(([l, q, tot]) => `<tr><td>${l}</td>${q.map((x) => `<td style="text-align:right">${money(x)}</td>`).join('')}<td style="text-align:right"><b>${money(tot)}</b></td></tr>`).join('');
    const lines = (cur.sections || []).map((s) => `<tr><td colspan="6" style="background:#f1f5f9"><b>${esc(s.name)}</b> (${s.kind})</td></tr>` + (s.lines || []).map((l) =>
      `<tr><td>${esc(l.name)}</td>${(l.amounts || [0, 0, 0, 0]).map((x) => `<td style="text-align:right">${money(x)}</td>`).join('')}<td style="text-align:right">${money((l.amounts || []).reduce((a, b) => a + Number(b || 0), 0))}</td></tr>`).join('')).join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>${esc(cur.name)}</title><style>body{font-family:Arial,sans-serif;font-size:12px;margin:24px}table{border-collapse:collapse;width:100%;margin:12px 0}td,th{border:1px solid #cbd5e1;padding:3px 6px}pre{white-space:pre-wrap;font-family:inherit}</style></head><body>
      <h2>${esc(orgName)} — ${esc(cur.name)} (${esc(cur.season)}) · Version ${cur.version} · ${esc(cur.status)}</h2>
      <pre>${esc(renderResolution(cur, orgName))}</pre>
      <h3>Summary</h3><table><tr><th></th>${ql.map((q) => `<th>${q}</th>`).join('')}<th>Total</th></tr>${rows}</table>
      <p>Break-even fee per player: ${t.break_even_fee == null ? '—' : money(t.break_even_fee)} · Reserve goal (${t.reserve_pct}% of revenue): ${money(t.reserve_goal)}</p>
      <h3>Line items</h3><table><tr><th></th>${ql.map((q) => `<th>${q}</th>`).join('')}<th>Total</th></tr>${lines}</table></body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  const STATUS_BTN = { draft: [['submit', 'Submit to board'], ['approve', 'Mark approved']], submitted: [['approve', 'Mark approved'], ['reopen', 'Reopen']], approved: [['revise', 'New version']] };
  const ro = cur && ['submitted', 'approved'].includes(cur.status);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-3" data-testid="ad-finance-budget">
      <div className="space-y-2">
        <button type="button" className={`${btnPrimary} w-full justify-center`} onClick={() => setCreating(true)} data-testid="budget-new"><Plus className="h-4 w-4" /> New budget</button>
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 max-h-[calc(100vh-380px)] overflow-auto bg-white dark:bg-slate-900/40">
          {list === null && <div className="p-3 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div>}
          {list?.length === 0 && <div className="p-3 text-xs text-slate-500">No budgets yet.</div>}
          {(list || []).map((b) => (
            <button key={b.id} type="button" onClick={() => pick(b.id)} data-testid={`budget-item-${b.id}`}
              className={`w-full text-left px-3 py-1.5 text-sm ${cur?.id === b.id ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
              <div className="font-medium text-slate-900 dark:text-white truncate">{b.name}</div>
              <div className="text-[11px] text-slate-500 flex justify-between gap-1"><span>{b.season || '—'} · v{b.version} {b.status}</span>
                <span className={b.totals.net_planned < 0 ? 'text-red-600' : 'text-emerald-600'}>{money(b.totals.net_planned)}</span></div>
            </button>
          ))}
        </div>
        <button type="button" className={`${btnGhost} w-full justify-center text-xs`} onClick={() => setTplOpen(true)}><FileStack className="h-4 w-4" /> Templates ({templates.length + (starter ? 1 : 0)})</button>
      </div>

      <div className="min-w-0">
        {!cur ? (
          <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-8 text-center text-sm text-slate-500">
            Create a budget — from the Season Budget Planner starter, one of your templates, or blank.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <input value={cur.name} disabled={ro} onChange={(e) => edit({ name: e.target.value })} className={`${inputCls} py-1 font-semibold w-56`} data-testid="budget-name" />
              <input value={cur.season || ''} disabled={ro} onChange={(e) => edit({ season: e.target.value })} placeholder="2026-2027" className={`${inputCls} py-1 w-24`} data-testid="budget-season" />
              <label className="text-[11px] text-slate-500 flex items-center gap-1">FY starts
                <select value={cur.settings?.fiscal_start_month || 1} disabled={ro} onChange={(e) => setSetting('fiscal_start_month', Number(e.target.value))} className={`${inputCls} py-0.5 text-xs`}>
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select></label>
              <label className="text-[11px] text-slate-500 flex items-center gap-1">Reserve
                <input type="number" min="0" max="100" value={cur.settings?.reserve_pct ?? 0} disabled={ro} onChange={(e) => setSetting('reserve_pct', e.target.value)} className={`${inputCls} py-0.5 text-xs w-14`} />%</label>
              <label className="text-[11px] text-slate-500 flex items-center gap-1" title="Flag a line in Actuals vs budget when it is this % past its budget">Flag over
                <input type="number" min="0" value={cur.settings?.variance_alert_pct ?? ''} disabled={ro} onChange={(e) => setSetting('variance_alert_pct', e.target.value)} className={`${inputCls} py-0.5 text-xs w-14`} data-testid="budget-variance-pct" />%</label>
              <label className="text-[11px] text-slate-500 flex items-center gap-1" title="One sport's budget (its teams fill the calculators; its lines link to that sport's accounts) or all sports">Sport
                <select value={cur.settings?.sport || ''} disabled={ro} onChange={(e) => setSetting('sport', e.target.value)} className={`${inputCls} py-0.5 text-xs`}>
                  <option value="">All sports</option>{sports.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                </select></label>
              <div className="ml-auto flex flex-wrap gap-1.5">
                {(STATUS_BTN[cur.status] || []).map(([a, l]) => (
                  <button key={a} type="button" className={`${btnGhost} py-1 text-xs`} disabled={dirty} title={dirty ? 'Save first' : ''}
                    onClick={() => setStatusAct(a)} data-testid={`budget-status-${a}`}>{l}</button>
                ))}
                <button type="button" className={`${btnGhost} py-1 px-2`} onClick={() => setSharing(true)} title="Member links" data-testid="budget-share"><Share2 className="h-4 w-4" /></button>
                <button type="button" className={`${btnGhost} py-1 px-2`} onClick={saveTemplate} title="Save as template"><FileStack className="h-4 w-4" /></button>
                <button type="button" className={`${btnGhost} py-1 px-2`} onClick={del} title="Delete budget"><Trash2 className="h-4 w-4" /></button>
                <button type="button" className={`${btnPrimary} py-1`} disabled={busy || !dirty} onClick={save} data-testid="budget-save">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </button>
              </div>
            </div>
            <BudgetPlanner budget={cur} onChange={edit} orgName={orgName} onFill={fill} onPrint={print}
              onLoadAccounts={loadAccounts} onSaveLinks={saveLinks} />
            <div className="text-[11px] text-slate-500">Version {cur.version} · {cur.status} · last saved {String(cur.updated_at || '').slice(0, 16).replace('T', ' ')} by {cur.updated_by || '—'}</div>
          </div>
        )}
      </div>

      {creating && <NewBudgetDialog base={base} templates={templates} starter={starter} onClose={() => setCreating(false)}
        onDone={async (b) => { setCreating(false); await loadList(); open(b.id); }} />}
      {sharing && cur && <ShareDialog base={base} budget={cur} onClose={() => setSharing(false)} />}
      {statusAct && cur && <StatusDialog base={base} budget={cur} action={statusAct} onClose={() => setStatusAct(null)}
        onDone={(b) => { setStatusAct(null); setCur(b); setDirty(false); loadList(); }} />}
      {tplOpen && (
        <Modal title="Budget templates" onClose={() => setTplOpen(false)}>
          <p className="text-xs text-slate-500 mb-2">A template is a budget's format: sections, line names, calculators (rows and unit costs) and resolution — no amounts. Make one with “Save as template” on any budget.</p>
          {starter && <div className="py-1.5 text-sm border-t border-slate-100 dark:border-slate-800">{starter.name} <span className="text-[11px] text-slate-400">· built in · {starter.sections} sections · {starter.lines} lines · {starter.calculators} calculators</span></div>}
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-1.5 border-t border-slate-100 dark:border-slate-800 text-sm">
              <span>{t.name} <span className="text-[11px] text-slate-400">· {t.sections.length} sections · {t.sections.reduce((a, s) => a + s.lines.length, 0)} lines · {(t.calculators || []).length} calculators</span></span>
              <button type="button" className="text-slate-400 hover:text-red-600" title="Delete template"
                onClick={async () => { if (!window.confirm(`Delete template “${t.name}”?`)) return; await axios.delete(`${base}/budget-templates/${t.id}`); loadList(); }}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}

const STATUS_COPY = {
  submit: ['Submit to the board', 'Locks the budget while the board reviews it. Reopen it to change anything first.'],
  approve: ['Mark approved by the board', 'Locks this version and keeps a copy of it. Any later change starts a new version that goes back to the board (the resolution).'],
  reopen: ['Reopen for changes', 'Back to draft — the board has not approved it yet.'],
  revise: ['Start a new version', 'Copies the approved budget into a new draft version. Say what changes — it is recorded with the version.'],
};

function StatusDialog({ base, budget, action, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [title, help] = STATUS_COPY[action];
  const go = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${base}/budgets/${budget.id}/status`, { action, note, approved_on: on }); toast.success(title); onDone(data.budget); }
    catch (e) { toast.error(errMsg(e, 'Could not change the status')); } finally { setBusy(false); }
  };
  return (
    <Modal title={`${title} — ${budget.name} v${budget.version}`} onClose={onClose}>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{help}</p>
      {action === 'approve' && <label className="text-xs block mb-2">Board approval date<input type="date" value={on} onChange={(e) => setOn(e.target.value)} className={`${inputCls} block mt-0.5`} /></label>}
      <label className="text-xs block mb-3">{action === 'revise' ? 'What changes (required)' : 'Note (optional)'}
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} w-full mt-0.5`} data-testid="budget-status-note" /></label>
      <div className="flex justify-end"><button type="button" className={btnPrimary} disabled={busy || (action === 'revise' && !note.trim())} onClick={go} data-testid="budget-status-go">{title}</button></div>
    </Modal>
  );
}

function NewBudgetDialog({ base, templates, starter, onClose, onDone }) {
  const [name, setName] = useState('');
  const [season, setSeason] = useState('');
  const [tid, setTid] = useState(starter ? starter.id : (templates[0]?.id || ''));
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${base}/budgets`, { name, season, template_id: tid || null }); onDone(data.budget); }
    catch (e) { toast.error(errMsg(e, 'Could not create the budget')); } finally { setBusy(false); }
  };
  return (
    <Modal title="New budget" onClose={onClose}>
      <div className="space-y-3">
        <label className="text-xs block">Name<input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} w-full mt-0.5`} data-testid="budget-new-name" /></label>
        <label className="text-xs block">Season<input value={season} onChange={(e) => setSeason(e.target.value)} placeholder="2026-2027" className={`${inputCls} w-full mt-0.5`} data-testid="budget-new-season" /></label>
        <label className="text-xs block">Start from
          <select value={tid} onChange={(e) => setTid(e.target.value)} className={`${inputCls} w-full mt-0.5`} data-testid="budget-new-template">
            {starter && <option value={starter.id}>{starter.name} — {starter.calculators} calculators, {starter.lines} lines</option>}
            {templates.map((t) => <option key={t.id} value={t.id}>Template: {t.name}</option>)}
            <option value="">Blank (Income + Expenses)</option>
          </select>
        </label>
        <p className="text-[11px] text-slate-500">The fiscal year follows your OL Finance company; change it on the budget.</p>
        <div className="flex justify-end"><button type="button" className={btnPrimary} disabled={busy || !name.trim()} onClick={create} data-testid="budget-create">Create</button></div>
      </div>
    </Modal>
  );
}

function ShareDialog({ base, budget, onClose }) {
  const [shares, setShares] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [send, setSend] = useState(true);
  const [busy, setBusy] = useState(false);
  const url = `${base}/budgets/${budget.id}/shares`;
  const load = useCallback(async () => {
    try { const { data } = await axios.get(url); setShares(data.shares || []); } catch (e) { toast.error(errMsg(e, 'Could not load links')); setShares([]); }
  }, [url]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(url, { name, email, send: send && !!email });
      navigator.clipboard?.writeText(data.share.url);
      toast.success(data.emailed ? 'Link created and emailed' : 'Link created and copied');
      setName(''); setEmail(''); load();
    } catch (e) { toast.error(errMsg(e, 'Could not create the link')); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Member links — ${budget.name}`} onClose={onClose} wide>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
        Each person gets a personal link to add, edit and delete lines in this budget — no login needed. The Treasurer sees the same budget under Finance. Revoke a link at any time.
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="text-xs flex-1 min-w-[140px]">Name<input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} w-full mt-0.5`} data-testid="share-name" /></label>
        <label className="text-xs flex-1 min-w-[180px]">Email<input value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} w-full mt-0.5`} data-testid="share-email" /></label>
        <label className="text-xs flex items-center gap-1 pb-2"><input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} /> Email the link</label>
        <button type="button" className={btnPrimary} disabled={busy || !name.trim()} onClick={add} data-testid="share-create"><Plus className="h-4 w-4" /> Create link</button>
      </div>
      <div className="max-h-64 overflow-auto">
        {shares === null ? <Loader2 className="h-4 w-4 animate-spin" /> : shares.length === 0 ? <div className="text-sm text-slate-500">No member links yet.</div> : (
          <table className="w-full text-sm"><tbody>
            {shares.map((s) => (
              <tr key={s.token} className="border-t border-slate-100 dark:border-slate-800">
                <td className="py-1.5 pr-2 font-medium">{s.name}</td>
                <td className="pr-2 text-xs text-slate-500">{s.email || '—'}</td>
                <td className="pr-2 text-xs text-slate-500">{s.last_used_at ? `used ${String(s.last_used_at).slice(0, 10)}` : 'not used yet'}</td>
                <td className="text-right whitespace-nowrap">
                  <button type="button" title="Copy link" className="p-1 text-slate-400 hover:text-blue-600" onClick={() => { navigator.clipboard?.writeText(s.url); toast.success('Link copied'); }}><Copy className="h-4 w-4" /></button>
                  <button type="button" title="Revoke" className="p-1 text-slate-400 hover:text-red-600"
                    onClick={async () => { if (!window.confirm(`Revoke ${s.name}'s link?`)) return; await axios.delete(`${url}/${s.token}`); load(); }}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </Modal>
  );
}
